import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { GraduationCap, Star, Linkedin, Youtube, Instagram, PlayCircle, Sparkles, ArrowRight } from "lucide-react";

const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

export default function FacultySection() {
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/faculty/public", { params: { limit: 12 } })
      .then(({ data }) => setFaculty(data.items || []))
      .catch(() => setFaculty([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || faculty.length === 0) return null;

  return (
    <section className="relative py-20 md:py-24 border-t border-white/5 overflow-hidden" data-testid="faculty-section">
      <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] bg-[#FF0066]/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> Expert Faculty
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white" style={displayStack}>
            Meet Our Expert <span className="text-[#FF0066]">Faculty</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-white/65 max-w-2xl mx-auto">
            Learn from industry practitioners who have built real businesses, run successful campaigns and trained thousands of students.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {faculty.map((f) => (
            <FacultyCard key={f.id} f={f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FacultyCard({ f }) {
  const social = f.social_links || {};
  return (
    <Link
      to={`/faculty/${f.slug || f.id}`}
      className="group relative bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/40 rounded-2xl overflow-hidden flex flex-col transition hover:-translate-y-1"
      data-testid={`faculty-card-${f.id}`}
    >
      {f.is_featured && (
        <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
          <Star className="w-2.5 h-2.5 fill-current" /> Featured
        </div>
      )}
      {/* Profile image with banner */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-[#FF0066]/30 via-[#FF0066]/10 to-[#050505] overflow-hidden">
        {f.profile_image ? (
          <img src={f.profile_image} alt={f.full_name} loading="lazy" className="w-full h-full object-cover transition duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <GraduationCap className="w-20 h-20 text-[#FF0066]/40" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        {f.demo_lecture_url && (
          <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-black/60 backdrop-blur-sm border border-white/15 text-white text-[10px] rounded-full px-2 py-0.5">
            <PlayCircle className="w-3 h-3 text-[#FF0066]" /> Watch demo
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-bold text-white leading-tight" style={displayStack}>{f.full_name}</h3>
        {f.designation && <div className="mt-1 text-xs text-[#FF0066] font-medium">{f.designation}</div>}
        {f.specialization && <div className="mt-1.5 text-[11px] text-white/55 leading-snug line-clamp-2">{f.specialization}</div>}

        <div className="mt-3 flex items-center gap-3 text-[11px] text-white/65">
          {f.experience_years > 0 && (
            <span><b className="text-white">{f.experience_years}+</b> yrs exp</span>
          )}
          {f.rating > 0 && (
            <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-current" /> {f.rating} {f.review_count > 0 && <span className="text-white/40">({f.review_count})</span>}</span>
          )}
        </div>

        {Array.isArray(f.achievements) && f.achievements.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {f.achievements.slice(0, 3).map((a, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] text-white/65 border border-white/10">{a}</span>
            ))}
          </div>
        )}

        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {social.linkedin && <a href={social.linkedin} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-white/40 hover:text-blue-400" aria-label="LinkedIn"><Linkedin className="w-3.5 h-3.5" /></a>}
            {social.youtube  && <a href={social.youtube}  target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-white/40 hover:text-red-400"  aria-label="YouTube"><Youtube  className="w-3.5 h-3.5" /></a>}
            {social.instagram&& <a href={social.instagram} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-white/40 hover:text-pink-400" aria-label="Instagram"><Instagram className="w-3.5 h-3.5" /></a>}
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-[#FF0066] font-semibold opacity-0 group-hover:opacity-100 transition">View profile <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </Link>
  );
}
